/**
 * ImageLightbox — xem ảnh phóng to ngay trong trang, không mở tab mới.
 *
 * Đóng bằng Esc / click nền / nút ✕. Chuyển ảnh bằng ← → , nút ‹ ›, dải
 * thumbnail hoặc vuốt ngang trên điện thoại. Không dùng thư viện ngoài để giữ
 * bundle nhỏ.
 *
 * Chỉ mount khi index >= 0 (caller giữ state) nên khi đóng không tốn DOM.
 * z-[10001] đứng trên Dialog/Sheet (z-50) và QuickInfoPopup (z-[10000]) —
 * ảnh ghi chú thường được mở từ trong Sheet lịch chụp.
 *
 * Vẽ qua PORTAL thẳng vào <body> (đo 27/09/2026): hộp thoại trên máy tính
 * căn giữa bằng `translate`, mà phần tử `fixed` nằm trong khối có transform
 * thì bị giam trong khối đó — mở ảnh từ trong hộp thoại (khiếu nại, chụp bằng
 * chứng, ảnh concept…) chỉ ra một khung 510×630 thay vì phủ cả màn 1440×900.
 * Sự kiện React vẫn nổi theo cây React qua portal, nên Radix vẫn coi bấm trong
 * ảnh là bấm TRONG hộp thoại và không tự đóng hộp thoại phía sau.
 * `pointer-events-auto`: hộp thoại modal của Radix khoá `pointer-events` của
 * <body> — portal không bật lại thì mọi nút trong ảnh bấm không ăn.
 *
 * Ảnh vẽ bằng Bộ soi ảnh (`components/photo-viewer`, spec 2026-10-28, CEO chốt 1b): phóng tới 800%,
 * kính lúp, bản đồ nhỏ, công cụ soi; `origHref` (link ảnh gốc) là bậc nét nhất khi phóng sâu.
 * Kéo / vuốt trên ảnh ĐANG PHÓNG là để soi — bộ soi chặn lại, không cho thành vuốt chuyển ảnh.
 * "So nhiều tấm" bày 2–6 ảnh cạnh nhau, phóng chung một mức (kiểu lưới so của Lightroom).
 */

import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { ladderFromUrls, type PhotoLadder } from "@/components/photo-viewer/ladder";
import { PhotoViewer } from "@/components/photo-viewer/PhotoViewer";
import { FIT_VIEW, type ViewState } from "@/components/photo-viewer/types";

/** Vuốt ngắn hơn ngần này thì coi là chạm, không chuyển ảnh. */
const SWIPE_MIN_PX = 40;

export function ImageLightbox({
  urls,
  index,
  onClose,
  onIndex,
  thumbUrls,
  origHref,
  caption,
  actions,
  ladders,
  variant = "default",
}: {
  /** Ảnh bản lớn, hiển thị chính giữa. */
  urls: string[];
  /** Ảnh đang xem; < 0 hoặc ngoài mảng => không render gì. */
  index: number;
  onClose: () => void;
  onIndex: (i: number) => void;
  /** Bản nhỏ cho dải thumbnail; thiếu thì dùng luôn `urls`. */
  thumbUrls?: string[] | undefined;
  /** Link ảnh gốc theo index — bậc nét nhất khi phóng sâu + lối mở ảnh gốc ở tab mới. */
  origHref?: ((i: number) => string) | undefined;
  /** Thang bậc ảnh theo index khi màn có sẵn nhiều cỡ (kho album); thiếu thì dựng từ `urls` + `origHref`. */
  ladders?: ((i: number) => PhotoLadder) | undefined;
  /** Chú thích dưới ảnh đang xem (ai chụp, lúc nào…). */
  caption?: ((i: number) => ReactNode) | undefined;
  /** Nút thao tác trên ảnh đang xem, nằm cạnh chú thích. */
  actions?: ((i: number) => ReactNode) | undefined;
  /**
   * "photo": xem ảnh kiểu điện thoại — nền đen ĐẶC (không nhìn xuyên trang phía
   * sau), "Ảnh i / n" ở trên, nút đóng tròn 44px, không dải ảnh con, không dòng
   * phím tắt. Theo bản mẫu ảnh giao ca CEO duyệt 23/09/2026. Mặc định giữ nguyên.
   */
  variant?: "default" | "photo" | undefined;
}) {
  const count = urls.length;
  const open = index >= 0 && index < count;
  const swipe = useSwipe(count, index, onIndex);
  const photo = variant === "photo";
  const [survey, setSurvey] = useState<number[] | null>(null);
  const ladderAt = (i: number) => ladders?.(i) ?? ladderFromUrls(urls[i] ?? "", origHref?.(i));

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
      if (e.key === "ArrowLeft") onIndex(Math.max(0, index - 1));
      if (e.key === "ArrowRight") onIndex(Math.min(count - 1, index + 1));
    }
    // capture: Dialog/Sheet của Radix cũng nghe Esc ở document. Không chặn
    // trước thì bấm Esc để đóng ảnh sẽ đóng luôn cả panel bên dưới.
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, index, count, onIndex, onClose]);

  // Khoá cuộn nền: overlay chiếm toàn màn, cuộn phía sau chỉ gây lạc chỗ.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;
  const thumbs = thumbUrls && thumbUrls.length === count ? thumbUrls : urls;
  const captionNode = caption?.(index);
  const actionsNode = actions?.(index);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Xem ảnh"
      className={
        photo
          ? "pointer-events-auto fixed inset-0 z-[10001] flex flex-col items-center bg-[#0c0a08] px-2 pb-[calc(16px+env(safe-area-inset-bottom,0px))] pt-[60px] text-white"
          : "pointer-events-auto fixed inset-0 z-[10001] flex flex-col items-center justify-center bg-black/90 p-4"
      }
      onClick={onClose}
      {...swipe}
    >
      {photo ? (
        <PhotoTopBar index={index} count={count} onClose={onClose} />
      ) : (
        <TopBar
          href={origHref ? origHref(index) : null}
          onClose={onClose}
          survey={count > 1 ? survey !== null : undefined}
          onSurvey={() => setSurvey((cur) => (cur ? null : defaultSurvey(index, count)))}
        />
      )}

      {survey ? (
        <SurveyGrid picks={survey} ladderAt={ladderAt} />
      ) : (
        <Stage ladder={ladderAt(index)} index={index} count={count} onIndex={onIndex} photo={photo} />
      )}

      <CaptionBar caption={captionNode} actions={actionsNode} wide={photo} />

      {photo ? (
        count > 1 ? (
          <div className="mt-2 w-full px-2 text-xs text-white/78 md:hidden">Vuốt ngang để xem ảnh khác</div>
        ) : null
      ) : (
        <>
          {count > 1 && (
            <ThumbStrip
              thumbs={thumbs}
              index={index}
              onIndex={onIndex}
              picks={survey ?? undefined}
              onPick={(i) => setSurvey((cur) => (cur ? togglePick(cur, i) : cur))}
            />
          )}
          <div className="mt-2 text-xs text-white/70">
            {survey
              ? `So ${survey.length} tấm · bấm ảnh ở dải dưới để thêm / bớt (2–6 tấm) · lăn chuột trên một tấm là cả lưới cùng phóng`
              : `${count > 1 ? `${index + 1} / ${count} · ← → để chuyển ảnh, ` : ""}lăn chuột để phóng, giữ Z để soi kính lúp, Esc để đóng`}
          </div>
        </>
      )}
    </div>,
    document.body,
  );
}

/** Ảnh đang xem (Bộ soi ảnh) + hai nút ‹ ›. */
function Stage(props: { ladder: PhotoLadder; index: number; count: number; onIndex: (i: number) => void; photo: boolean }) {
  const { ladder, index, count, onIndex, photo } = props;
  return (
    <div
      className={
        photo
          ? "relative flex min-h-0 w-full flex-1 items-center justify-center"
          : "relative flex h-[80vh] w-full max-w-[1400px] items-center justify-center"
      }
    >
      {count > 1 && (
        <NavButton
          side="left"
          photo={photo}
          disabled={index === 0}
          onClick={(e) => {
            e.stopPropagation();
            onIndex(Math.max(0, index - 1));
          }}
        />
      )}
      {/* Khoá theo chỉ số: sang ảnh khác là bộ soi về vừa khung, không mang mức phóng của tấm trước. */}
      <PhotoViewer key={index} ladder={ladder} alt="" autoFocus className={photo ? "rounded" : undefined} />
      {count > 1 && (
        <NavButton
          side="right"
          photo={photo}
          disabled={index === count - 1}
          onClick={(e) => {
            e.stopPropagation();
            onIndex(Math.min(count - 1, index + 1));
          }}
        />
      )}
    </div>
  );
}

/** Lưới so nhiều tấm: mọi tấm dùng CHUNG một mức phóng / chỗ soi. */
function SurveyGrid({ picks, ladderAt }: { picks: number[]; ladderAt: (i: number) => PhotoLadder }) {
  const [view, setView] = useState<ViewState>(FIT_VIEW);
  const cols = picks.length <= 2 ? "md:grid-cols-2" : picks.length <= 4 ? "grid-cols-2" : "grid-cols-2 md:grid-cols-3";
  return (
    <div
      className={`grid h-[80vh] w-full max-w-[1400px] grid-cols-1 gap-1 ${cols}`}
      role="group"
      aria-label={`So ${picks.length} tấm`}
      onClick={(e) => e.stopPropagation()}
    >
      {picks.map((i, n) => (
        <div key={i} className="relative min-h-0 overflow-hidden rounded bg-black/40">
          <PhotoViewer
            ladder={ladderAt(i)}
            alt={`Ảnh ${i + 1}`}
            view={view}
            onViewChange={setView}
            toolbar={n === 0 ? "compact" : "none"}
          />
          <span className="pointer-events-none absolute left-2 top-2 rounded bg-black/60 px-2 py-0.5 text-xs text-white">
            Ảnh {i + 1}
          </span>
        </div>
      ))}
    </div>
  );
}

const SURVEY_MAX = 6;

/** Mở lưới so: tấm đang xem + tấm kế bên (tối đa 4 để mỗi khung còn đủ to). */
function defaultSurvey(index: number, count: number): number[] {
  const start = Math.max(0, Math.min(index, count - 4));
  return Array.from({ length: Math.min(4, count) }, (_, k) => start + k);
}

function togglePick(cur: number[], i: number): number[] {
  if (cur.includes(i)) return cur.length > 2 ? cur.filter((x) => x !== i) : cur;
  return cur.length >= SURVEY_MAX ? cur : [...cur, i].sort((a, b) => a - b);
}

/** Vuốt ngang trên điện thoại để chuyển ảnh. */
function useSwipe(count: number, index: number, onIndex: (i: number) => void) {
  const start = useRef<{ x: number; y: number } | null>(null);
  return {
    onTouchStart: (e: React.TouchEvent) => {
      const t = e.touches[0];
      start.current = e.touches.length === 1 && t ? { x: t.clientX, y: t.clientY } : null;
    },
    onTouchEnd: (e: React.TouchEvent) => {
      const from = start.current;
      const t = e.changedTouches[0];
      start.current = null;
      if (!from || !t || count < 2) return;
      const dx = t.clientX - from.x;
      // Chỉ nhận vuốt NGANG rõ ràng — vuốt chéo/dọc là người ta đang cuộn.
      if (Math.abs(dx) < SWIPE_MIN_PX || Math.abs(dx) <= Math.abs(t.clientY - from.y)) return;
      onIndex(dx < 0 ? Math.min(count - 1, index + 1) : Math.max(0, index - 1));
    },
  };
}

/** Chú thích + nút thao tác dưới ảnh đang xem. Không có gì thì không chiếm chỗ. */
function CaptionBar({ caption, actions, wide }: { caption: ReactNode; actions: ReactNode; wide: boolean }) {
  if (!caption && !actions) return null;
  return (
    <div
      className={`mt-3 flex w-full flex-wrap items-center justify-between gap-x-4 gap-y-2.5 text-white ${
        wide ? "px-2" : "max-w-[1100px]"
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="min-w-0">{caption}</div>
      {actions}
    </div>
  );
}

/** Kiểu "photo": đếm ảnh bên trái, nút đóng tròn 44px bên phải — vừa ngón tay. */
function PhotoTopBar({ index, count, onClose }: { index: number; count: number; onClose: () => void }) {
  return (
    <div
      className="absolute inset-x-0 top-0 flex items-center justify-between gap-3 py-2 pl-4 pr-2"
      onClick={(e) => e.stopPropagation()}
    >
      <span className="text-sm tabular-nums text-white/78">
        Ảnh {index + 1} / {count}
      </span>
      <button
        type="button"
        className="grid h-11 w-11 place-items-center rounded-full bg-white/14 text-xl leading-none text-white hover:bg-white/20"
        onClick={onClose}
        aria-label="Đóng"
      >
        ✕
      </button>
    </div>
  );
}

/** Nút đóng + so nhiều tấm + lối thoát sang ảnh gốc ở tab mới khi cần tải về. */
function TopBar({
  href,
  onClose,
  survey,
  onSurvey,
}: {
  href: string | null;
  onClose: () => void;
  /** undefined = chỉ một ảnh, không có nút so. */
  survey?: boolean | undefined;
  onSurvey: () => void;
}) {
  return (
    <div className="absolute right-3 top-3 z-10 flex items-center gap-2">
      {survey !== undefined && (
        <button
          type="button"
          aria-pressed={survey}
          onClick={(e) => {
            e.stopPropagation();
            onSurvey();
          }}
          className={`rounded px-3 py-1 text-xs text-white hover:bg-white/20 ${survey ? "bg-white/30" : "bg-white/10"}`}
        >
          {survey ? "Xem từng tấm" : "So nhiều tấm"}
        </button>
      )}
      {href && (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="rounded bg-white/10 px-3 py-1 text-xs text-white hover:bg-white/20"
        >
          Ảnh gốc
        </a>
      )}
      <button
        type="button"
        className="rounded bg-white/10 px-3 py-1 text-white hover:bg-white/20"
        onClick={onClose}
        aria-label="Đóng"
      >
        ✕
      </button>
    </div>
  );
}

function ThumbStrip({
  thumbs,
  index,
  onIndex,
  picks,
  onPick,
}: {
  thumbs: string[];
  index: number;
  onIndex: (i: number) => void;
  /** Đang so nhiều tấm: các tấm đang trong lưới; bấm là thêm / bớt thay vì chuyển ảnh. */
  picks?: number[] | undefined;
  onPick?: (i: number) => void;
}) {
  return (
    <div className="mt-3 flex gap-2 overflow-x-auto" onClick={(e) => e.stopPropagation()}>
      {thumbs.map((u, i) => (
        <button
          key={`${u}-${i}`}
          type="button"
          onClick={() => (picks && onPick ? onPick(i) : onIndex(i))}
          aria-label={`Ảnh ${i + 1}`}
          aria-current={i === index}
          aria-pressed={picks ? picks.includes(i) : undefined}
          className={`h-12 w-16 shrink-0 overflow-hidden rounded border-2 ${
            (picks ? picks.includes(i) : i === index) ? "border-white" : "border-transparent opacity-60"
          }`}
        >
          <img src={u} alt="" className="h-full w-full object-cover" />
        </button>
      ))}
    </div>
  );
}

function NavButton({
  side,
  photo,
  disabled,
  onClick,
}: {
  side: "left" | "right";
  photo: boolean;
  disabled: boolean;
  onClick: (e: React.MouseEvent) => void;
}) {
  // Kiểu "photo" đè lên ảnh nên cần viền + nền tối để nổi trên mọi ảnh.
  const look = photo
    ? `${side === "left" ? "left-3" : "right-3"} border border-white/45 bg-[rgba(12,10,8,0.62)] pb-[3px] text-[26px] disabled:opacity-28`
    : `${side === "left" ? "left-0" : "right-0"} bg-white/10 text-2xl hover:bg-white/20 disabled:opacity-30`;
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-label={side === "left" ? "Ảnh trước" : "Ảnh sau"}
      className={`absolute z-10 flex h-11 w-11 items-center justify-center rounded-full leading-none text-white ${look}`}
    >
      {side === "left" ? "‹" : "›"}
    </button>
  );
}
